import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import axiosApi from "../../../../../../utils/axios";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";
import FileUpload from "../../../../../../Components/common/FileUpload/FileUpload";
import Warning from "../../../../../../Components/common/Warning/Warning";
import { addNotification } from "../../../../../../store/actions/notifierActions";
import { requestRestoreStatus } from "../../../../../../store/actions/settingsActions";

import {
  formatDuration,
  formatSize,
  isSameFile,
  runningFor,
  uploadMissingChunks,
  UploadPaused,
} from "./restoreUpload";

const BUSY_POLL_INTERVAL = 5000;
const IDLE_POLL_INTERVAL = 15000;
const RESTORE_WARNING =
  "Restoring replaces the current data with the data in the backup. Submissions and changes made while the restore is running will be lost. Do you want to continue?";

const Restore = () => {
  const dispatch = useDispatch();

  const restoreStatus = useSelector((state) => state.settings.restoreStatus);
  const restoreInfo = useSelector((state) => state.settings.restoreInfo);
  const dumpStatus = useSelector((state) => state.settings.dumpStatus);
  const pendingUpload = restoreInfo.upload;

  const [file, setFile] = useState(null);
  const [restoreFileError, setRestoreFileError] = useState("");
  const [confirm, setConfirm] = useState(null); // { text, onConfirm }
  const [phase, setPhase] = useState(""); // "", preparing, uploading, pausing, verifying, starting
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [savedBytes, setSavedBytes] = useState(0);
  const [activeFile, setActiveFile] = useState(null);
  const abortRef = useRef(null);
  const pauseRef = useRef(null);
  const rateRef = useRef(null);
  const resumeInputRef = useRef(null);

  const isRestoring = restoreStatus === "restoring";
  const isWorking = phase !== "";

  const pendingBytes = pendingUpload
    ? (pendingUpload.receivedBytes ??
      Math.min(pendingUpload.size, pendingUpload.receivedCount * pendingUpload.chunkSize))
    : 0;
  const pendingPercent = pendingUpload ? Math.floor((pendingBytes / pendingUpload.size) * 100) : 0;

  useEffect(() => {
    const fetchData = () => dispatch(requestRestoreStatus());

    fetchData();

    const interval = setInterval(fetchData, isRestoring ? BUSY_POLL_INTERVAL : IDLE_POLL_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, isRestoring]);

  // The upload is kept on the server, so leaving the page only pauses it
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!isWorking) return undefined;
    const warnBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [isWorking]);

  const notifyError = (error, fallback) =>
    dispatch(
      addNotification({
        message: error?.response?.data?.message ?? fallback,
        options: { variant: "error" },
      }),
    );

  const progressHandler = ({ sent, saved }) => {
    if (!rateRef.current) rateRef.current = { time: Date.now(), bytes: sent };
    setUploadedBytes(sent);
    setSavedBytes(saved);
  };

  // selectedFile is null when a finished upload only needs its restore started
  const uploadAndRestore = async (selectedFile) => {
    const controller = new AbortController();
    const pauseController = new AbortController();
    abortRef.current = controller;
    pauseRef.current = pauseController;
    rateRef.current = null;
    const resuming = !selectedFile || isSameFile(selectedFile, pendingUpload);
    let saved = resuming ? pendingBytes : 0;
    setUploadedBytes(saved);
    setSavedBytes(saved);
    setActiveFile(selectedFile);
    setPhase("preparing");

    try {
      let upload = pendingUpload?.complete && resuming ? pendingUpload : null;

      if (!upload) {
        const { data: session } = await axiosApi.post(
          "/api/settings/restore/uploads",
          {
            fileName: selectedFile.name,
            size: selectedFile.size,
            lastModified: selectedFile.lastModified,
          },
          { signal: controller.signal },
        );

        setPhase("uploading");
        await uploadMissingChunks({
          file: selectedFile,
          upload: session,
          onProgress: (progress) => {
            saved = progress.saved;
            progressHandler(progress);
          },
          signal: controller.signal,
          pauseSignal: pauseController.signal,
        });

        setPhase("verifying");
        const { data } = await axiosApi.post(
          `/api/settings/restore/uploads/${session.id}/complete`,
          null,
          { signal: controller.signal },
        );
        upload = data;
      }

      setPhase("starting");
      const response = await axiosApi.post("/api/settings/restore", { upload_id: upload.id });
      dispatch(
        addNotification({ message: response.data.message, options: { variant: "success" } }),
      );
      setFile(null);
    } catch (error) {
      if (error instanceof UploadPaused || controller.signal.aborted)
        dispatch(
          addNotification({
            message: `Upload paused at ${Math.floor(
              (saved / (selectedFile?.size || pendingUpload?.size || 1)) * 100,
            )}%. You can continue from where it stopped.`,
            options: { variant: "info" },
          }),
        );
      else notifyError(error, "Upload failed. Click Resume to continue from where it stopped.");
    } finally {
      abortRef.current = null;
      pauseRef.current = null;
      setPhase("");
      setActiveFile(null);
      dispatch(requestRestoreStatus());
    }
  };

  const discardUpload = async (uploadId) => {
    try {
      await axiosApi.delete(`/api/settings/restore/uploads/${uploadId}`);
    } catch (error) {
      notifyError(error, "Discarding upload failed");
    } finally {
      dispatch(requestRestoreStatus());
    }
  };

  // Lets the chunks being sent finish so the upload continues exactly from where it was paused
  const pauseHandler = () => {
    pauseRef.current?.abort();
    setPhase("pausing");
  };

  const confirmOkHandler = () => {
    if (!confirm) return;
    const { onConfirm } = confirm;
    setConfirm(null);
    onConfirm();
  };

  const restoreButtonClickHandler = () => {
    // Continuing an upload was already confirmed when it was started
    if (isSameFile(file, pendingUpload)) {
      uploadAndRestore(file);
      return;
    }
    let discardNote = "";
    if (pendingUpload)
      discardNote = pendingUpload.complete
        ? ` The uploaded file ${pendingUpload.fileName} will be discarded.`
        : ` The paused upload of ${pendingUpload.fileName} (${pendingPercent}%) will be discarded.`;
    setConfirm({ text: RESTORE_WARNING + discardNote, onConfirm: () => uploadAndRestore(file) });
  };

  const resumeClickHandler = () => {
    if (pendingUpload.complete) {
      setConfirm({ text: RESTORE_WARNING, onConfirm: () => uploadAndRestore(null) });
    } else if (isSameFile(file, pendingUpload)) {
      uploadAndRestore(file);
    } else {
      // The browser forgets the selected file on reload, so ask for it again
      resumeInputRef.current?.click();
    }
  };

  const resumeFileChangeHandler = (e) => {
    const selectedFile = e.target.files?.[0];
    e.target.value = "";
    if (!selectedFile) return;
    if (!isSameFile(selectedFile, pendingUpload)) {
      setRestoreFileError(
        `Select the same file to continue: ${pendingUpload.fileName} (${formatSize(
          pendingUpload.size,
        )}).`,
      );
      return;
    }
    setRestoreFileError("");
    setFile(selectedFile);
    uploadAndRestore(selectedFile);
  };

  const discardClickHandler = () =>
    setConfirm({
      text: `Discard the upload of ${pendingUpload.fileName}? The uploaded part will be deleted and the next upload starts from the beginning.`,
      onConfirm: () => discardUpload(pendingUpload.id),
    });

  const resumable = pendingUpload && isSameFile(file, pendingUpload);
  const buttonLabel = isRestoring
    ? "Restoring..."
    : dumpStatus === "dumping"
      ? "Backuping..."
      : resumable
        ? "Continue Upload & Restore"
        : "Upload & Restore";

  const restoreInfoText = () => {
    if (isRestoring) {
      const elapsed = runningFor(restoreInfo.startedAt);
      return (
        <div className="restore__body__info">
          Restore in progress. This can take a while{elapsed && ` (${elapsed})`}.
        </div>
      );
    }
    if (restoreStatus === "success")
      return (
        <div className="restore__body__info restore__body__info--success">
          Last restore finished successfully. {restoreInfo.message}
        </div>
      );
    if (restoreStatus === "error")
      return (
        <div className="restore__body__info restore__body__info--error">
          Last restore failed. {restoreInfo.message}
        </div>
      );
    return null;
  };

  const pendingUploadInfo = pendingUpload && !isWorking && !isRestoring && (
    <div className="restore__pending">
      <span>
        {pendingUpload.complete
          ? `${pendingUpload.fileName} was uploaded but the restore was not started.`
          : `Upload of ${pendingUpload.fileName} paused at ${pendingPercent}% (${formatSize(
              pendingBytes,
            )} / ${formatSize(pendingUpload.size)}).`}
      </span>
      <button
        className="primary-button"
        onClick={resumeClickHandler}
        disabled={dumpStatus === "dumping"}>
        {pendingUpload.complete ? "Start Restore" : "Resume"}
      </button>
      <button className="danger-button" onClick={discardClickHandler}>
        Discard
      </button>
      <input
        ref={resumeInputRef}
        type="file"
        accept=".gz"
        style={{ display: "none" }}
        onChange={resumeFileChangeHandler}
      />
    </div>
  );

  const total = activeFile?.size || 0;
  const percent = total ? Math.min(100, Math.floor((uploadedBytes / total) * 100)) : 0;
  const rate = rateRef.current;
  const elapsed = rate ? (Date.now() - rate.time) / 1000 : 0;
  const speed = elapsed > 1 ? (uploadedBytes - rate.bytes) / elapsed : 0;
  const eta = speed > 0 ? (total - uploadedBytes) / speed : Infinity;

  const phaseText = {
    preparing: "Preparing upload...",
    uploading: "Uploading. You can pause and continue later from where it stopped.",
    pausing: "Pausing... waiting for the chunks being sent to finish.",
    verifying: "Verifying the uploaded file...",
    starting: "Starting restore...",
  }[phase];

  const uploadMessage = (
    <div className="upload-message">
      <p className="upload-message__info">{phaseText}</p>
      {activeFile && (
        <>
          <div className="upload-message__header">
            <span className="upload-message__file-name">{activeFile.name}</span>
            <div className="upload-message__info">
              <span className="upload-message__loaded">{formatSize(uploadedBytes)} </span>/
              <span className="upload-message__total"> {formatSize(total)}</span>
            </div>
          </div>
          <div className="upload-item__progress">
            {percent}%
            {phase === "uploading" &&
              speed > 0 &&
              ` · ${formatSize(speed)}/s · ${formatDuration(eta)} left`}
          </div>
          {(phase === "uploading" || phase === "pausing") && (
            <div className="upload-message__saved">
              Saved on server: {formatSize(savedBytes)}. If the page is closed, the upload continues
              from here.
            </div>
          )}
        </>
      )}
      {phase === "uploading" && (
        <div className="upload-message__actions">
          <button className="primary-button" onClick={pauseHandler}>
            Pause
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="restore">
      <h1 className="restore__header">Restore</h1>
      <div className="restore__body">
        <div className="restore__body__header">
          <h3>Restore</h3>
          <p>
            NOTE: Please upload the .gz file you download to restore your system. Restoring database
            can take a while. Submissions and changes made while the restore is running will be
            lost.
          </p>
        </div>
        <div className="restore__body__content">
          {restoreFileError && <p>{restoreFileError}</p>}
          {restoreInfoText()}
          {pendingUploadInfo}
          <div className="restore__body__actions">
            <FileUpload
              file={file}
              setFile={setFile}
              setFileError={setRestoreFileError}
              allowedExtensions={["gz"]}
              errorMessage={`Please select .gz file that you downloaded from our system.`}
            />
            <button
              className="primary-button"
              onClick={restoreButtonClickHandler}
              disabled={!file || isWorking || isRestoring || dumpStatus === "dumping"}>
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
      {/* Starts the message 90px below the centre, clear of the 100px spinner however long it gets */}
      <Backdrop
        show={isWorking}
        message={uploadMessage}
        messageStyle={{ top: "50%", paddingTop: "90px" }}
      />
      <Warning
        onClickOk={confirmOkHandler}
        show={Boolean(confirm)}
        setShow={(show) => !show && setConfirm(null)}
        text={confirm?.text}
        article="Warning"
        cancelDisable={false}
      />
    </div>
  );
};

export default Restore;
